/**
 * Unified issue reporting for v3: local log (30-day retention) + WhatsApp prefill helpers.
 * WhatsApp support UI is gated by {@link isIssueReportWhatsappEnabled} (future variant).
 */
import { Platform } from 'react-native';
import { Linking } from 'react-native';
import { environment } from '../../shared/config/environment';
import { useAuthStore } from '../../shared/auth/store';
import { appendIssueReportEntry } from './issueReportLog';
import type { IssueDeviceSnapshot, IssueReportContext, IssueReportEntry, IssueReportKind } from './issueReportTypes';

export type { IssueDeviceSnapshot, IssueReportContext, IssueReportEntry, IssueReportKind } from './issueReportTypes';

const DEDUPE_WINDOW_MS = 2 * 60 * 1000;
const DEFAULT_DIALING_CODE = '965';
const recent_report_keys = new Map<string, number>();

/** Future variant — hide WhatsApp support action from end users until launched. */
export const ISSUE_REPORT_WHATSAPP_ENABLED = false;

export function isIssueReportWhatsappEnabled(): boolean {
  return ISSUE_REPORT_WHATSAPP_ENABLED;
}

/** Lazy read — avoids `client → issueReport → initial/api → client` require cycle at module load. */
function readOrganization() {
  const { getOrganization } = require('../../data/domains/config') as typeof import('../../data/domains/config');
  return getOrganization();
}

function createEntryId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildDedupeKey(context: IssueReportContext): string {
  return [
    context.kind ?? 'unknown',
    context.page,
    context.issue,
    context.api_endpoint ?? '',
    context.http_method ?? '',
    context.http_status ?? '',
    (context.error_message ?? '').slice(0, 120),
  ].join('|');
}

function shouldSkipDuplicate(context: IssueReportContext): boolean {
  const key = buildDedupeKey(context);
  const now = Date.now();
  const last = recent_report_keys.get(key);
  if (last != null && now - last < DEDUPE_WINDOW_MS) {
    return true;
  }
  recent_report_keys.set(key, now);
  return false;
}

export function getHttpStatusFromError(error: unknown): number | undefined {
  const err = error as { response?: { status?: number } };
  const status = err?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

export function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  const err = error as { message?: string };
  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }
  return undefined;
}

/** Do not hammer the server when the failure is clearly server-side. */
export function shouldRetryPreferencesQuery(failure_count: number, error: unknown): boolean {
  const status = getHttpStatusFromError(error);
  if (status != null && status >= 500) {
    return false;
  }
  return failure_count < 2;
}

export function buildDeviceSnapshot(customer_id?: number): IssueDeviceSnapshot {
  return {
    platform: Platform.OS,
    os_version: Platform.Version,
    app_version: environment.APP_VERSION,
    app_ui_version: 'studio',
    customer_id,
    timestamp_iso: new Date().toISOString(),
  };
}

export function buildSupportWhatsappMessage(
  context: IssueReportContext,
  customer_id?: number,
): string {
  const org = readOrganization();
  const user = useAuthStore.getState().user;
  const resolved_customer_id = customer_id ?? user?.id;

  const lines = [
    'Hello, I need assistance with the app.',
    '',
    `Page: ${context.page}`,
    `Issue: ${context.issue}`,
  ];

  if (context.detail_lines?.length) {
    for (const line of context.detail_lines) {
      if (line.trim()) {
        lines.push(line);
      }
    }
  }

  if (context.api_endpoint) {
    lines.push(`API: ${context.api_endpoint}`);
  }
  if (context.http_method) {
    lines.push(`Method: ${context.http_method}`);
  }
  if (context.http_status != null) {
    lines.push(`HTTP status: ${context.http_status}`);
  }
  if (context.error_message) {
    lines.push(`Error: ${context.error_message}`);
  }
  if (resolved_customer_id != null) {
    lines.push(`Customer ID: ${resolved_customer_id}`);
  }
  if (user?.name?.trim()) {
    lines.push(`Customer name: ${user.name.trim()}`);
  }
  if (user?.phone?.trim()) {
    const dc = String(org?.dialing_code ?? DEFAULT_DIALING_CODE).replace(/\D/g, '');
    lines.push(`Customer phone: +${dc} ${user.phone.trim()}`);
  }
  if (user?.mail?.trim()) {
    lines.push(`Customer email: ${user.mail.trim()}`);
  }
  lines.push(`App: ${environment.APP_VERSION}`);
  lines.push(`Device: ${Platform.OS} ${Platform.Version}`);
  lines.push(`Time: ${new Date().toISOString()}`);
  return lines.join('\n');
}

function normalizeWhatsappPhone(raw: string | undefined, dialing_code: string | undefined): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const dc = String(dialing_code ?? DEFAULT_DIALING_CODE).replace(/\D/g, '');
  if (dc && digits.startsWith(dc)) {
    return digits;
  }
  if (dc) {
    return `${dc}${digits}`;
  }
  return digits;
}

/** Resolve support WhatsApp number from org config with sensible fallbacks. */
export function resolveWhatsappSupportPhone(): string | null {
  const org = readOrganization();
  const general = org?.configurations?.general;
  const dialing_code = org?.dialing_code ?? DEFAULT_DIALING_CODE;

  const candidates = [general?.whatsapp_number, general?.phone, org?.phone];
  for (const candidate of candidates) {
    const phone = normalizeWhatsappPhone(candidate, dialing_code);
    if (phone) {
      return phone;
    }
  }
  return null;
}

export function resolveWhatsappSupportUrl(message: string): string | null {
  if (!isIssueReportWhatsappEnabled()) {
    return null;
  }
  const phone = resolveWhatsappSupportPhone();
  if (!phone) {
    return null;
  }
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export async function openWhatsappSupport(message: string): Promise<boolean> {
  if (!isIssueReportWhatsappEnabled()) {
    return false;
  }
  const phone = resolveWhatsappSupportPhone();
  if (!phone) {
    if (__DEV__) {
      console.warn('[IssueReport] WhatsApp support phone is not configured in org settings');
    }
    return false;
  }

  const encoded_message = encodeURIComponent(message);
  const urls = [
    `https://wa.me/${phone}?text=${encoded_message}`,
    `https://api.whatsapp.com/send?phone=${phone}&text=${encoded_message}`,
    `whatsapp://send?phone=${phone}&text=${encoded_message}`,
  ];

  for (const url of urls) {
    try {
      await Linking.openURL(url);
      return true;
    } catch {
      // try next URL scheme
    }
  }

  if (__DEV__) {
    console.warn('[IssueReport] Failed to open WhatsApp for phone', phone);
  }
  return false;
}

/**
 * Persist an issue locally. Safe to call from interceptors and UI; dedupes rapid repeats.
 * Only active when v3 UI is running.
 */
export function reportIssue(context: IssueReportContext, customer_id?: number): void {
  if (shouldSkipDuplicate(context)) {
    return;
  }
  const reported_at = new Date().toISOString();
  const resolved_customer_id = customer_id ?? useAuthStore.getState().user?.id;
  const entry: IssueReportEntry = {
    id: createEntryId(),
    reported_at,
    ...context,
    device: buildDeviceSnapshot(resolved_customer_id),
  };
  void appendIssueReportEntry(entry);
}

export function issueContextFromApiError(
  page: string,
  issue: string,
  error: unknown,
  options?: {
    api_endpoint?: string;
    http_method?: string;
    kind?: IssueReportKind;
  },
): IssueReportContext {
  return {
    page,
    issue,
    kind: options?.kind ?? 'api',
    api_endpoint: options?.api_endpoint,
    http_method: options?.http_method,
    http_status: getHttpStatusFromError(error),
    error_message: getErrorMessage(error),
  };
}

export function reportApiClientError(
  error: unknown,
  config?: { url?: string; method?: string; params?: unknown },
): void {
  const method = (config?.method ?? 'GET').toUpperCase();
  const raw_url = config?.url ?? '';
  const path = raw_url.replace(environment.API_URL ?? '', '').split('?')[0].replace(/^\//, '') || raw_url;
  const status = getHttpStatusFromError(error);
  if (status === 401) {
    return;
  }
  let endpoint = path;
  if (config?.params && typeof config.params === 'object') {
    const query = Object.entries(config.params as Record<string, unknown>)
      .filter(([, v]) => v != null)
      .map(([k, v]) => `${k}=${String(v)}`)
      .join('&');
    if (query) {
      endpoint = `${path}?${query}`;
    }
  }
  reportIssue({
    page: 'API',
    issue: `${method} ${path} failed`,
    kind: 'api',
    api_endpoint: endpoint,
    http_method: method,
    http_status: status,
    error_message: getErrorMessage(error),
  });
}
